import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Platform } from 'react-native'

import { i18n } from '@localization'
import { StackActions } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import { B2, Button, Stack } from '@zet/design-system'
import { useGenerateSharableLink } from '@zet/feature-brands/api/brands/hooks'
import UserConsent from '@zet/feature-brands/components/userConsent'
import { useApplyLink } from '@zet/feature-brands/hooks/useApplyLink'
import useMissingFieldsSheetStore from '@zet/feature-brands/store/missingFieldsSheet'
import { navigationRef } from '@zet/utils/navigation'
import { getName } from '@zet/utils/stringConversion'
import { useForm, useFormState } from 'react-hook-form'

import { convertFormDataToPayload } from '@components/missingFieldsPopup/utils/convertFormDataToPayload'

import { useSubmitMissingFields } from '../../../../api/missingFieldsPopup/hooks'
import { setFormData } from '../../utils/setFormValues'
import Form from './Form'
import { ajvResolver } from './resolver'

export const FormBuilder = (props) => {
  const { mutate } = useSubmitMissingFields()
  const { closeSheet } = useMissingFieldsSheetStore()
  const [consentBottomSheetVisible, setConsentBottomSheetVisible] =
    useState(false)

  const ocFields = props?.ocFields
  const validationSchema = props?.validationSchema || {}
  const fieldValues = props?.fieldValues

  const openConsentBottomSheet = () => {
    setConsentBottomSheetVisible(true)
  }

  const closeConsentBottomSheet = () => {
    setConsentBottomSheetVisible(false)
  }

  const { control, handleSubmit, setValue } = useForm({
    resolver: ajvResolver(validationSchema),
    mode: 'onTouched',
  })

  const { isValid } = useFormState({
    control,
  })
  function handleLeadSource() {
    return props?.leadSource
  }
  const {
    mutate: fetchShareLink,
    isLoading,
    isSuccess,
    data: shareLinkData,
  } = useGenerateSharableLink()

  const { handleApply } = useApplyLink(
    shareLinkData,
    props?.brandId,
    props?.customerName,
    props?.customerId,
    props?.customerPhoneNumber,
    props?.leadSource,
    openConsentBottomSheet,
    closeConsentBottomSheet,
    false,
    true,
    props?.variantId
  )

  useEffect(() => {
    if (isSuccess && shareLinkData) {
      // While the lead is still being evaluated (shouldPoll), the sheet must
      // stay mounted — usePoll's timer lives in this component and unmounting
      // it kills the polling loop.
      if (shareLinkData?.shouldPoll) {
        return
      }
      if (shareLinkData?.statusCode !== 3361) {
        closeSheet()
      }
      handleApply()
    }
  }, [isSuccess, shareLinkData])

  // With no missing fields the customer's data is already complete and the
  // partner is evaluating eligibility — re-hit generateLink once (it polls
  // via usePoll) to fetch the outcome while the checking state shows.
  const hasFiredEligibilityCheck = useRef(false)
  useEffect(() => {
    if (props?.isEligibilityCheck && !hasFiredEligibilityCheck.current) {
      hasFiredEligibilityCheck.current = true
      fetchShareLink({
        partnerId: Number(props?.brandId),
        customerId: props?.customerId,
        leadSource: handleLeadSource(),
        variantId: props?.variantId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.isEligibilityCheck])

  const onSubmit = (data) => {
    publishEvent('click_action', {
      item_name: 'missing_details_form_submit',
      brand_name: props?.brandName,
      partner_id: props?.brandId,
    })
    const convertedData = convertFormDataToPayload(data)
    mutate(
      {
        formData: convertedData?.data,
        customerId: props?.customerId,
      },
      {
        onSuccess: (data) => {
          if (Platform.OS === 'web') {
            fetchShareLink({
              partnerId: Number(props?.brandId),
              customerId: props?.customerId,
              leadSource: handleLeadSource(),
              variantId: props?.variantId,
            })
          } else {
            closeSheet()
            navigationRef.dispatch(
              StackActions.replace('ShareCardLink', {
                generateLinkParams: props?.generateLinkParams,
                routeName: 'missingFields',
                brandId: props?.brandId,
                customerName: getName(props?.customerName),
                customerId: props?.customerId,
              })
            )
          }
        },
      }
    )
  }

  useMemo(() => {
    if (ocFields?.length) {
      setFormData(fieldValues, setValue)
    }
  }, [fieldValues, ocFields])

  if (props?.isEligibilityCheck) {
    return (
      <Stack>
        <Stack className="h-[220px] items-center justify-center px-4">
          <ActivityIndicator size={'large'} color={'blue'} />
          <B2 className="text-grays-70 mt-3 text-center">
            {i18n.t('missingFieldsPopup.checkingEligibility')}
          </B2>
        </Stack>
        {consentBottomSheetVisible && (
          <UserConsent
            brandId={props?.brandId}
            customerId={props?.customerId}
            isBottomSheetVisible={consentBottomSheetVisible}
            setIsBottomSheetVisible={setConsentBottomSheetVisible}
            customerName={props?.customerName}
            leadSource={props?.leadSource}
            customerPhoneNumber={props?.customerPhoneNumber}
            generateLinkParams={props?.generateLinkParams}
          />
        )}
      </Stack>
    )
  }

  return (
    <Stack>
      <Form control={control} ocFields={ocFields} />
      <Stack className="px-2 pb-2">
        <Button
          size="lg"
          disabled={!isValid || !ocFields?.length}
          fullWidth
          onPress={handleSubmit(onSubmit)}
          loading={isLoading}
          eventName="click_action"
          eventPayload={{
            item_name: 'missing-details_submit',
            partner_id: props?.brandId,
            customer_id: props?.customerId,
            customer_name: props?.customerName,
            customer_phone_number: props?.customerPhoneNumber,
          }}
        >
          {i18n.t('missingFieldsPopup.continueApplication')}
        </Button>
      </Stack>
      {consentBottomSheetVisible && (
        <UserConsent
          brandId={props?.brandId}
          customerId={props?.customerId}
          isBottomSheetVisible={consentBottomSheetVisible}
          setIsBottomSheetVisible={setConsentBottomSheetVisible}
          customerName={props?.customerName}
          leadSource={props?.leadSource}
          customerPhoneNumber={props?.customerPhoneNumber}
          generateLinkParams={props?.generateLinkParams}
        />
      )}
    </Stack>
  )
}
